import { RefObject, useEffect, useRef } from 'react';
import { useMediaQuery } from '@mui/material';

import type { BookingState } from '../bookingState';

interface BookingTransitionSnapshot {
  scopeKey: string;
  state: BookingState | 'review';
}

interface BookingTransitionFocusRefs {
  headingRef: RefObject<HTMLDivElement>;
  contentRef: RefObject<HTMLDivElement>;
}

interface BookingTransitionFocusTargets {
  heading: Pick<HTMLDivElement, 'focus'> | null;
  content: Pick<HTMLDivElement, 'scrollTo'> | null;
  reduceMotion: boolean;
}

const FOCUSABLE_BOOKING_STATES: ReadonlySet<BookingState> = new Set(['held', 'confirmed', 'cancelled', 'failed']);

/**
 * Solo mueve la lectura cuando la MISMA operación cruza a otro momento. Abrir
 * una reserva existente o cambiar de oferta no debe robar foco ni animar el
 * modal por el mero montaje del componente.
 */
export function shouldFocusBookingTransition(
  previous: BookingTransitionSnapshot,
  current: BookingTransitionSnapshot,
): boolean {
  return (
    previous.scopeKey === current.scopeKey &&
    previous.state !== current.state &&
    FOCUSABLE_BOOKING_STATES.has(current.state as BookingState)
  );
}

export function applyBookingTransitionFocus({
  heading,
  content,
  reduceMotion,
}: BookingTransitionFocusTargets): void {
  content?.scrollTo({
    top: 0,
    behavior: reduceMotion ? 'auto' : 'smooth',
  });
  heading?.focus({ preventScroll: true });
}

export function useBookingTransitionFocus(
  scopeKey: string,
  state: BookingState | 'review',
): BookingTransitionFocusRefs {
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const headingRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const previousRef = useRef<BookingTransitionSnapshot>({ scopeKey, state });

  useEffect(() => {
    const current = { scopeKey, state };
    const shouldFocus = shouldFocusBookingTransition(previousRef.current, current);
    previousRef.current = current;
    if (!shouldFocus) return;

    const frame = window.requestAnimationFrame(() => {
      applyBookingTransitionFocus({
        content: contentRef.current,
        heading: headingRef.current,
        reduceMotion,
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [reduceMotion, scopeKey, state]);

  return { headingRef, contentRef };
}
